import { User, AuthState, AuthResponse } from '../types';
import { apiService } from './api';

// Initial auth state
const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: false,
  error: null
};

class AuthService {
  // Login user
  async login(username: string, password: string): Promise<AuthState & { maintenanceMode?: boolean }> {
    try {
      const response = await apiService.login(username, password);
      
      // Map the AuthResponse to User
      const user: User = {
        id: response.userId,
        userId: response.userId,
        username: response.username,
        firstName: response.firstName,
        lastName: response.lastName,
        email: response.email,
        profilePicture: response.profilePicture,
        permissions: response.permissions,
        password: '', // We don't store the password in the client
        active: true, // Assume active if they can login
        firstLogin: response.firstLogin || false, // Use from response or default to false
        tempPassword: null // Don't store temp password on client
      };
      
      return {
        user,
        token: response.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        maintenanceMode: response.maintenanceMode
      };
    } catch (error) {
      return {
        ...initialState,
        error: 'Invalid credentials'
      };
    }
  }
  
  // Logout user
  logout(): AuthState {
    apiService.clearToken();
    localStorage.removeItem('token');
    return initialState;
  }
  
  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      return await apiService.validateToken(token);
    } catch (error) {
      localStorage.removeItem('token');
      return false;
    }
  }
  
  // Get current user from token
  async getCurrentUser(): Promise<AuthState> {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, returning unauthenticated state');
      return initialState;
    }
    
    try {
      console.log('Token found, attempting to relogin with token...');
      
      // Set the token in the API service
      apiService.setToken(token);
      
      // Validate token
      const isValid = await apiService.validateToken(token);
      
      if (!isValid) {
        console.log('Token invalid, removing token and returning unauthenticated state');
        localStorage.removeItem('token');
        apiService.clearToken();
        return initialState;
      }
      
      // Get user from API
      const user = await apiService.getCurrentUser();
      
      if (!user) {
        console.log('User not found, removing token and returning unauthenticated state');
        localStorage.removeItem('token');
        apiService.clearToken();
        return initialState;
      }
      
      console.log('User authenticated:', user.username);
      
      // Store token in localStorage again to refresh it
      localStorage.setItem('token', token);
      
      return {
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    } catch (error) {
      console.error('Error validating token or fetching user:', error);
      localStorage.removeItem('token');
      apiService.clearToken();
      return initialState;
    }
  }
  
  // Check if user has permission
  async hasPermission(permission: string): Promise<boolean> {
    try {
      const user = await apiService.getCurrentUser();
      if (!user || !user.permissions) {
        return false;
      }
      
      // Check if the permission exists in the user's permissions
      return user.permissions.some(p => p === permission as any);
    } catch (error) {
      return false;
    }
  }
}

export const authService = new AuthService();
